import { useCallback } from "react";
import { downloadVideo, handleDownloadError } from "@src/features/clips";
import type { Clip } from "@src/features/clips";
import type { CompilationListItem } from "@visioni/a3s-media/app";

export interface UseCompilationWatchResult {
  /** `null` until a ready item with a signed video URL is being watched. */
  readonly clip: Clip | null;
  /** `undefined` when there's nothing to download yet (e.g. a not-yet-redeployed Lambda). */
  readonly handleDownload: (() => void) | undefined;
}

/**
 * Shapes a `CompilationListItem` into what the app-specific `ClipVideoPlayer`/
 * download flow need — the surrounding layout is `CompilationWatchView` from
 * `@visioni/a3s-media/app`, which knows nothing about `expo-video` or this
 * app's download utility.
 */
export function useCompilationWatch(item: CompilationListItem | null): UseCompilationWatchResult {
  const handleDownload = useCallback(() => {
    if (!item?.downloadUrl) return;
    void downloadVideo({ videoUrl: item.downloadUrl, timestamp: new Date() }).catch(handleDownloadError);
  }, [item]);

  const clip: Clip | null =
    item?.videoUrl ?
      { id: item.id, timestamp: new Date().toISOString(), thumbnailUrl: null, videoUrl: item.videoUrl, duration: 0 }
    : null;

  return {
    clip,
    handleDownload: item?.downloadUrl ? handleDownload : undefined,
  };
}
